import { fmtDollar, fmtDate } from './formatters';
import { formatEstimatedCashToCloseLine } from './estimatedCashToCloseDisplay';

/** Backend sends U+2014 when no LOS value */
export const isPlaceholderDash = (v) => {
  if (v == null || v === '') return true;
  const s = String(v).trim();
  return s === '—' || s === '-' || s === '–';
};

export const stripLeadingDisplayJunk = (s) =>
  String(s ?? '')
    .replace(/^[\s\u00A7\u2022\u00B7·•\uFEFF\u200B\u200C\u200D]+/g, '')
    .trim();

const humanize = (str) => (str ? String(str).replace(/([A-Z])/g, ' $1').trim() : null);

const feeAmount = (cc) => {
  if (!cc || typeof cc !== 'object') return 0;
  const borrower = (cc.borrowerPaidAtClose || 0) + (cc.borrowerPaidBeforeClose || 0);
  const lender = (cc.lenderPaidAtClose || 0) + (cc.lenderPaidBeforeClose || 0);
  const broker = (cc.brokerPaidAtClose || 0) + (cc.brokerPaidBeforeClose || 0);
  const seller = (cc.sellerPaidAtClose || 0) + (cc.sellerPaidBeforeClose || 0);
  const other = (cc.otherPaidAtClose || 0) + (cc.otherPaidBeforeClose || 0);
  const sum = borrower + lender + broker + seller + other;
  return sum || cc.totalAmount || 0;
};

export function collectOriginationChargesFees(closingCosts) {
  if (!closingCosts || typeof closingCosts !== 'object') return [];
  let hasAnyFeeCategory = false;
  const matched = [];
  for (const arr of Object.values(closingCosts)) {
    if (!Array.isArray(arr)) continue;
    for (const row of arr) {
      if (!row || typeof row !== 'object') continue;
      if ('feeCategory' in row && row.feeCategory != null) hasAnyFeeCategory = true;
      if (row.feeCategory === 'OriginationCharges') matched.push(row);
    }
  }
  if (matched.length > 0) return matched;
  if (!hasAnyFeeCategory) return (closingCosts.origination || []).filter(Boolean);
  return [];
}

const UW_DECISION_LABELS = {
  ConditionallyApprove: 'Approved with Conditions',
  ClearToClose: 'Clear to Close',
  Suspend: 'Suspended',
  Deny: 'Denied',
};

function formatUwDecision(code) {
  if (code == null || code === '') return '—';
  return UW_DECISION_LABELS[code] || String(code);
}

function formatInterestRatePriceCharge(loan) {
  const netPrice = loan?.loanOfficerPrice ?? loan?.basePrice;
  const loanAmount = loan?.loanAmount;
  if (netPrice == null || loanAmount == null || Number(loanAmount) <= 0) return '—';
  const creditOrCost = ((Number(netPrice) - 100) / 100) * Number(loanAmount);
  const abs = Math.abs(creditOrCost);
  const dollars = fmtDollar(abs);
  const priceStr = Number(netPrice).toFixed(3);
  if (creditOrCost >= 0) {
    return `Lender credit ${dollars} (net price ${priceStr} vs 100.000 par)`;
  }
  return `Discount points ${dollars} (net price ${priceStr} vs 100.000 par)`;
}

const CURRENCY_FIELD_KEY_RE =
  /amount|cost|charge|credit|fee|price|deposit|balance|premium|payment|points|cash|funds|comp/i;
const CURRENCY_LABEL_RE =
  /amount|cost|charge|credit|fee|price|deposit|balance|premium|payment|points|cash\s*to\s*close|funds|estimated/i;

function looksLikeCurrencyValue(raw) {
  const s = stripLeadingDisplayJunk(raw);
  if (!s) return false;
  if (/^\$[\d,]+(\.\d+)?$/.test(s)) return true;
  if (/^-?\$?[\d,]+(\.\d{2})?$/.test(s.replace(/\s/g, ''))) return true;
  if (/\d/.test(s) && /^\s*[\$€£]?\s*[\d,]+(\.\d+)?\s*$/.test(s.replace(/[^\d.,$-]/g, ''))) return true;
  return false;
}

/** Labels/keys that must never be dollar-formatted (plain text or integers). */
function isNonCurrencyDocOrderField(field) {
  const key = (field.key || '').replace(/\s/g, '').toLowerCase();
  const lab = (field.label || '').toLowerCase().trim();
  if (key === 'program' || lab === 'program') return true;
  if (key.includes('loantermmonths') || key.includes('loantermyears') || /loan\s*term/.test(lab)) return true;
  if (key.includes('amortization') || lab.includes('amortization')) return true;
  if (key.includes('loanpurpose') || lab === 'loan purpose') return true;
  if (key.includes('borrower') || lab.includes('borrower')) return true;
  if (key.includes('currentstage') || lab === 'loan stage' || lab.includes('loan stage')) return true;
  if (key.includes('subjectproperty') || lab.includes('subject property')) return true;
  if (key.includes('noterate') || lab === 'interest rate' || lab.includes('interest rate')) return true;
  if (key.includes('ltv') || key.includes('cltv') || key.includes('dti') || lab === 'ltv' || lab === 'cltv' || lab === 'dti') {
    return true;
  }
  if (key === 'totalcashtoclose' || lab.includes('total cash to close')) return true;
  return false;
}

function normalizeDisplayValue(raw, field) {
  if (raw == null) return '';
  const str = String(raw);
  if (isPlaceholderDash(str)) return str;

  const key = (field.key || '').toLowerCase();
  const lab = (field.label || '').toLowerCase();
  const stripped = stripLeadingDisplayJunk(str);
  if (/%/.test(stripped)) return stripped;
  if (isClosingDateField(field)) {
    const d = new Date(stripped);
    return Number.isNaN(d.getTime()) ? stripped : fmtDate(stripped);
  }
  if (isNonCurrencyDocOrderField(field)) return stripped;

  const tryCurrency =
    CURRENCY_FIELD_KEY_RE.test(key) ||
    CURRENCY_LABEL_RE.test(lab) ||
    looksLikeCurrencyValue(stripped);

  if (!tryCurrency) return stripped;

  const numeric = parseFloat(String(stripped).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numeric)) return stripped;
  return fmtDollar(numeric);
}

function isClosingDateField(field) {
  const key = (field.key || '').replace(/\s/g, '').toLowerCase();
  const lab = (field.label || '').toLowerCase();
  return key.includes('closingdate') || lab === 'closing date' || lab.includes('closing date');
}

function shouldDropField(field) {
  const key = (field.key || '').replace(/\s/g, '').toLowerCase();
  const lab = (field.label || '').toLowerCase().trim();
  if (key.includes('closingtime') || lab === 'closing time') return true;
  if (key.includes('closingnotes') || lab === 'closing notes') return true;
  if (key.includes('escrowlicense') || lab.includes('escrow license')) return true;
  if (key.includes('escrowaffiliatedwithbroker') || lab === 'broker owned' || lab.includes('broker owned')) {
    return true;
  }
  return false;
}

function getLoanFieldOverride(field, loan) {
  if (!loan) return undefined;
  const key = (field.key || '').replace(/\s/g, '').toLowerCase();
  const lab = (field.label || '').toLowerCase();

  if (
    key === 'estimatedcashtoclose' ||
    lab.includes('estimated cash to close') ||
    (lab.includes('cash to close') && !lab.includes('seller'))
  ) {
    return formatEstimatedCashToCloseLine(loan.estimatedCashToClose);
  }

  if (key === 'totalcashtoclose' || lab.includes('total cash to close')) {
    return formatEstimatedCashToCloseLine(loan.estimatedCashToClose);
  }

  if (key === 'underwriterdecision' || lab.includes('underwriting decision') || lab === 'uw decision') {
    return formatUwDecision(loan.underwriterDecision);
  }

  if (
    key.includes('underwriterdecisiondate') ||
    lab.includes('underwriting decision date') ||
    lab.includes('uw decision date')
  ) {
    return loan.underwriterDecisionDate ? fmtDate(loan.underwriterDecisionDate) : '—';
  }

  if (isClosingDateField(field)) {
    if (loan.estimatedCloseDate) return fmtDate(loan.estimatedCloseDate);
    return '—';
  }

  if (key === 'program' || lab === 'program') {
    const name = loan.programName || loan.productCode;
    if (name) return String(name);
  }

  if (key.includes('loantermmonths') || key.includes('loantermyears') || /loan\s*term/.test(lab)) {
    if (loan.loanTermMonths != null && loan.loanTermMonths !== '') {
      return String(Math.round(Number(loan.loanTermMonths)));
    }
  }

  return undefined;
}

function isInterestRatePriceChargeField(field) {
  const key = (field.key || '').toLowerCase();
  const lab = (field.label || '').toLowerCase();
  if (/interestrate.*(credit|charge|discount|points|adjust)/i.test(key)) return true;
  if (/interest\s*rate.*(credit|charge|discount|points)/i.test(lab)) return true;
  if (lab.includes('discount points') && lab.includes('interest')) return true;
  if (lab.includes('lender credit') && (lab.includes('rate') || lab.includes('price'))) return true;
  return false;
}

function shouldDropSection(section) {
  const t = (section.sectionTitle || '').toLowerCase();
  const k = (section.sectionKey || '').toLowerCase();
  if (/lender\s+services/.test(t)) return true;
  if (k.includes('lenderservice') || k.includes('lender_services')) return true;
  if (/required/.test(t) && /lender/.test(t) && /service/.test(t)) return true;
  return false;
}

function loanChargesSectionScore(section) {
  const t = (section.sectionTitle || '').toLowerCase();
  const k = (section.sectionKey || '').toLowerCase();
  if (k.includes('loancharge') || k.includes('loan_charge')) return 3;
  if (t.includes('loan charge')) return 3;
  if (/^a\.\s*origination/i.test(t.trim())) return 1;
  return 0;
}

function mapClosingCostRowsToFields(rows) {
  return rows.map((cc, i) => {
    const name =
      humanize(cc.feeType) ||
      humanize(cc.prepaidExpenseType) ||
      humanize(cc.initialEscrowPaymentType) ||
      cc.description ||
      'Fee';
    return {
      key: `origination-${cc.closingCostId ?? i}`,
      label: stripLeadingDisplayJunk(name),
      value: fmtDollar(feeAmount(cc)),
    };
  });
}

/** Stable id for pinning a field correction */
export function docOrderFieldPinId(sectionKey, fieldKey) {
  return `${sectionKey}::${fieldKey}`;
}

/**
 * @param {object|null} docOrder
 * @param {object|null} loan
 * @param {object[]|null} originationFeeRows
 */
export function processDocOrderSections(docOrder, loan, originationFeeRows = []) {
  if (!docOrder) return [];

  const rawSections =
    docOrder.sections?.length > 0
      ? docOrder.sections
      : docOrder.fields?.length > 0
        ? [{ sectionKey: 'docOrder', sectionTitle: 'Doc order', fields: docOrder.fields }]
        : [];

  const filtered = rawSections.filter((s) => !shouldDropSection(s));

  let bestIdx = -1;
  let bestScore = 0;
  filtered.forEach((section, idx) => {
    const sc = loanChargesSectionScore(section);
    if (sc > bestScore) {
      bestScore = sc;
      bestIdx = idx;
    }
  });
  const replaceLoanChargesIdx =
    originationFeeRows.length > 0 && bestIdx >= 0 && bestScore > 0 ? bestIdx : -1;

  const mapped = filtered.map((section, idx) => {
    if (idx === replaceLoanChargesIdx) {
      return {
        ...section,
        fields: mapClosingCostRowsToFields(originationFeeRows),
      };
    }

    const fields = (section.fields || [])
      .filter((field) => !shouldDropField(field))
      .map((field) => {
        const override = getLoanFieldOverride(field, loan);
        if (override !== undefined) {
          return { ...field, value: override };
        }
        if (isInterestRatePriceChargeField(field)) {
          return { ...field, value: formatInterestRatePriceCharge(loan) };
        }
        const v = normalizeDisplayValue(field.value, field);
        return { ...field, value: v };
      });

    return { ...section, fields };
  });

  if (replaceLoanChargesIdx < 0 && originationFeeRows.length > 0) {
    return [
      ...mapped,
      {
        sectionKey: 'loanChargesOrigination',
        sectionTitle: 'Loan charges (origination)',
        fields: mapClosingCostRowsToFields(originationFeeRows),
      },
    ];
  }

  return mapped;
}
